import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Leaf, ArrowRight, Info, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "Materials", label: "Materials & Supplies", factor: "0.50" },
  { value: "Utilities", label: "Utilities & Energy", factor: "0.233" },
  { value: "Travel", label: "Travel & Transport", factor: "0.21" },
  { value: "Labour", label: "Labour", factor: "0.05" },
  { value: "Subcontractors", label: "Subcontractors", factor: "0.30" },
  { value: "Equipment Rental", label: "Equipment Rental", factor: "0.40" },
  { value: "Repairs", label: "Repairs & Maintenance", factor: "0.35" },
  { value: "Office Supplies", label: "Office Supplies", factor: "0.20" },
  { value: "Insurance", label: "Insurance", factor: "0.02" },
  { value: "Other", label: "Other (AI Estimated)", factor: "AI" },
];

export default function CarbonCalculator() {
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const handleCalculate = async () => {
    if (!category || !amount || parseFloat(amount) <= 0) {
      toast.error("Please select a category and enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/carbon/calculate?category=${encodeURIComponent(category)}&amount=${parseFloat(amount)}`
      );
      setResult(response.data);
      setHistory((prev) => [
        { ...response.data, timestamp: new Date().toLocaleTimeString() },
        ...prev.slice(0, 4),
      ]);
      toast.success("Carbon footprint calculated");
    } catch (error) {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = CATEGORIES.find((c) => c.value === category);

  return (
    <div className="space-y-8" data-testid="calculator-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
          Carbon Calculator
        </h1>
        <p className="text-[#5c6b63] mt-2">
          Estimate carbon emissions from your business expenses
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Calculator Form */}
        <div className="lg:col-span-5">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[#1a2e22] font-['Outfit']">
                <Calculator className="w-5 h-5" />
                Calculate Emissions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category Select */}
              <div className="space-y-2">
                <Label htmlFor="category" className="text-[#1a2e22] font-medium">
                  Expense Category
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="category" data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center justify-between w-full">
                          <span>{cat.label}</span>
                          <span className="text-xs text-[#5c6b63] ml-2">
                            {cat.factor === "AI" ? "AI" : `${cat.factor} kg/£`}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount Input */}
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-[#1a2e22] font-medium">
                  Amount (£)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c6b63]">
                    £
                  </span>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="pl-8 input-field"
                    data-testid="amount-input"
                  />
                </div>
              </div>

              {/* Emission Factor Info */}
              {selectedCategory && (
                <div className="p-4 bg-[#f2f7f4] rounded-xl">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#5c6b63] mt-0.5" />
                    <div>
                      <p className="text-sm text-[#5c6b63]">
                        Emission factor for {selectedCategory.label}:
                      </p>
                      <p className="text-[#1a2e22] font-medium">
                        {selectedCategory.factor === "AI"
                          ? "AI-powered estimation"
                          : `${selectedCategory.factor} kg CO₂e per £`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Calculate Button */}
              <Button
                onClick={handleCalculate}
                disabled={loading || !category || !amount}
                className="w-full btn-accent flex items-center justify-center gap-2"
                data-testid="calculate-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Calculate
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-7 space-y-6">
          {/* Current Result */}
          {result && (
            <Card className="border-2 border-[#00c853]" data-testid="result-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <Leaf className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5c6b63]">Estimated Carbon Footprint</p>
                    <p className="text-4xl font-bold text-[#1a2e22] font-['Outfit']">
                      {result.carbon_kg.toFixed(2)}{" "}
                      <span className="text-lg font-normal">kg CO₂e</span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-[#f2f7f4] rounded-xl">
                    <p className="text-sm text-[#5c6b63]">Category</p>
                    <p className="font-medium text-[#1a2e22]">{result.category}</p>
                  </div>
                  <div className="p-4 bg-[#f2f7f4] rounded-xl">
                    <p className="text-sm text-[#5c6b63]">Amount</p>
                    <p className="font-medium text-[#1a2e22] font-mono">
                      £{parseFloat(result.amount).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 bg-[#f2f7f4] rounded-xl">
                    <p className="text-sm text-[#5c6b63]">Emission Scope</p>
                    <p
                      className={`font-medium ${
                        result.emission_scope === "Scope 1"
                          ? "text-amber-600"
                          : result.emission_scope === "Scope 2"
                          ? "text-blue-600"
                          : "text-emerald-600"
                      }`}
                    >
                      {result.emission_scope}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Calculation History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
                Recent Calculations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8">
                  <Calculator className="w-12 h-12 text-[#c8d4cc] mx-auto mb-4" />
                  <p className="text-[#5c6b63]">No calculations yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-[#f2f7f4] rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-[#1a2e22]">{item.category}</p>
                        <p className="text-sm text-[#5c6b63]">
                          £{parseFloat(item.amount).toFixed(2)} • {item.timestamp}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#1a2e22]">
                          {item.carbon_kg.toFixed(2)} kg
                        </p>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            item.emission_scope === "Scope 1"
                              ? "bg-amber-100 text-amber-700"
                              : item.emission_scope === "Scope 2"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {item.emission_scope}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scope Explanation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
                Understanding Emission Scopes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border-l-4 border-amber-400 bg-amber-50/50 rounded-r-xl">
                  <h4 className="font-semibold text-amber-800">Scope 1</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Direct emissions from owned or controlled sources
                  </p>
                </div>
                <div className="p-4 border-l-4 border-blue-400 bg-blue-50/50 rounded-r-xl">
                  <h4 className="font-semibold text-blue-800">Scope 2</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Indirect emissions from purchased energy
                  </p>
                </div>
                <div className="p-4 border-l-4 border-emerald-400 bg-emerald-50/50 rounded-r-xl">
                  <h4 className="font-semibold text-emerald-800">Scope 3</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    All other indirect emissions in the value chain
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
